import React, { useMemo, useRef } from 'react';

import type { CartItemType } from '@zola-helpers/client/dist/es/@types/cart';
import type { ConvertWebApiDeclaredToActual } from '@zola-helpers/client/dist/es/@types/svc-web-api';
import type { WProjectFullView, WProjectCustomizationFullView } from '@zola/svc-web-api-ts-client';
import {
  DigitalCanvasAdapter,
  DigitalCanvasProps,
} from '../../../../paper/cards/components/DigitalCanvas/DigitalCanvas';
import { useCartContext } from '../../CartContext';
import useContainerDimensions from '../../../../hooks/useContainerDimensions';
import { useComputedStyle } from '../../../../hooks/useComputedStyle/useComputedStyle';

/**
 * Sizes the DigitalCanvas image to fit the container.  Will not work for flex
 * containers.  In that case, pass in a width.
 */
const Customization = ({
  cardProject,
  className,
  customization,
  width,
  ...rest
}: {
  cardProject: ConvertWebApiDeclaredToActual<WProjectFullView>;
  customization: ConvertWebApiDeclaredToActual<WProjectCustomizationFullView>;
  className?: string;
} & Partial<DigitalCanvasProps>) => {
  const ref = useRef<HTMLDivElement>(null);

  const { clientWidth } = useContainerDimensions(ref);
  const { paddingLeft, paddingRight } = useComputedStyle(ref);

  // Since we're using a 1 / 1 aspect ratio, "maxDimension" will be used either as the
  // height or the width of the digital canvas, with the other automatically scaling.
  const maxDimension = useMemo(() => {
    return clientWidth - parseFloat(paddingLeft || '0') - parseFloat(paddingRight || '0');
  }, [clientWidth, paddingLeft, paddingRight]);

  return (
    <div className={className} ref={ref}>
      <DigitalCanvasAdapter
        cardProject={cardProject}
        customization={customization}
        width={width || maxDimension}
        limitHeight
        disableBleed
        aspectRatioValue="1"
        {...rest}
      />
    </div>
  );
};

export const CardItemImage = ({
  item,
  ...rest
}: { item: CartItemType; className?: string } & Partial<DigitalCanvasProps>) => {
  const { getCardCustomization, getCardProject } = useCartContext();

  const cardProject = useMemo(
    () => getCardProject(item.projectUuid as string),
    [getCardProject, item.projectUuid]
  );

  const customization = useMemo(() => {
    return getCardCustomization(item.projectUuid as string, item.customizationUuid as string);
  }, [getCardCustomization, item.customizationUuid, item.projectUuid]);

  if (!customization || !cardProject) {
    return null;
  }

  return <Customization cardProject={cardProject} customization={customization} {...rest} />;
};
